import React, { useMemo } from "react";
import CohortCinemaMap from './CohortCinemaMap';
import TopCompetenciesTable from './TopCompetenciesTable';
import CategoryDistributionTable from './CategoryDistributionTable';
import { useAllCompetencies } from '../../api/CompetenciesAPI';

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const styles = {
  page: {
    width: '210mm', // A4 width
    minHeight: '297mm', // A4 height
    margin: '0',
    padding: '20mm',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    backgroundColor: 'white',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  header: {
    marginBottom: '25px'
  },
  title: {
    fontSize: '20px',
    fontWeight: 900,
    marginBottom: '15px',
    color: '#333'
  },
  cohortDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px'
  },
  detailsText: {
    fontSize: '12px',
    color: '#666'
  },
  rightAlign: {
    textAlign: 'right'
  },
  detailLine: {
    marginBottom: '4px'
  },
  divider: {
    borderBottom: '1px solid #e0e0e0',
    marginTop: '8px',
    marginBottom: '4px'
  },
  sectionHeader: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#333'
  }
};

const CohortReportWeb = ({ cohortResponses, cohort, participants }) => {
  // Fetch all competencies for processing top competencies
  const { data: allCompetenciesData } = useAllCompetencies();

  // Process top competencies data
  const topCompetenciesData = useMemo(() => {
    if (!cohortResponses?.competency_counts || !allCompetenciesData) {
      return {};
    }

    // Create lookup map for competency details
    const competencyMap = new Map();
    allCompetenciesData.forEach(comp => {
      competencyMap.set(comp.id, {
        name: comp.name,
        category: comp.category,
        abilityName: comp.abilities?.name || 'Unknown',
        phase: comp.abilities?.phase || 'Unknown'
      });
    });

    // Group competency counts by phase
    const phaseGroups = {};
    const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    
    phases.forEach(phase => {
      phaseGroups[phase] = [];
    });

    // Process each competency count
    cohortResponses.competency_counts.forEach(countItem => {
      const competencyDetails = competencyMap.get(countItem.competency_id);
      if (competencyDetails) {
        const phase = competencyDetails.phase;
        const percentage = Math.round((countItem.count / (participants?.length || 1)) * 100);
        
        phaseGroups[phase]?.push({
          competencyId: countItem.competency_id,
          abilityName: competencyDetails.abilityName,
          competencyName: competencyDetails.name,
          category: competencyDetails.category,
          count: countItem.count,
          percentage: percentage
        });
      }
    });

    // Sort each phase by count (descending) and take top 10
    phases.forEach(phase => {
      if (phaseGroups[phase]) {
        phaseGroups[phase].sort((a, b) => {
          // Primary sort by count (descending)
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          // Secondary sort by competency name (ascending) for ties
          return a.competencyName.localeCompare(b.competencyName);
        });
        // Take top 10
        phaseGroups[phase] = phaseGroups[phase].slice(0, 10);
      }
    });

    return phaseGroups;
  }, [cohortResponses, allCompetenciesData, participants]);

  return (
    <div className="h-full p-6 flex flex-col gap-6 overflow-y-auto bg-gray-100">
      <div className="w-full min-h-0 flex-1">
        <div className="space-y-6">
          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.title}>Cohort Competency Mapping Report</p>

              <div style={styles.cohortDetailsContainer}>
                <div style={styles.detailsText}>
                  <div style={styles.detailLine}>
                    Cohort ID: {cohort?.id || 'N/A'}
                  </div>
                  <div style={styles.detailLine}>
                    Cohort Name: {cohort?.name || 'Untitled Cohort'}
                  </div>
                  <div style={styles.detailLine}>
                    Created: {formatDate(cohort?.created_at)}
                  </div>
                  <div style={styles.detailLine}>
                    Status: {cohort?.status || 'N/A'}
                  </div>
                </div>

                <div style={{...styles.detailsText, ...styles.rightAlign}}>
                  <div style={styles.detailLine}>
                    Total Participants: {participants?.length || 0}
                  </div>
                  <div style={styles.detailLine}>
                    Completed Sessions: {participants?.filter(p => p.status === 'completed')?.length || 0}
                  </div>
                  <div style={styles.detailLine}>
                    Total Competencies Selected: {cohortResponses?.response_count || 0}
                  </div>
                  <div style={styles.detailLine}>
                    Unique Competencies: {cohortResponses?.unique_competencies || 0}
                  </div>
                </div>
              </div>

              <div style={styles.divider} />
            </div>
            <div>
              <p style={styles.sectionHeader}>The Big Picture</p>
              <CohortCinemaMap cohortResponses={cohortResponses} />
            </div>
          </div>

          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.title}>Top Selected Competencies by Phase</p>
              <div style={styles.divider} />
            </div>
            <TopCompetenciesTable 
              topCompetenciesData={topCompetenciesData}
              totalParticipants={participants?.length || 0}
            />
          </div>

          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.title}>Category Analysis</p>
              <div style={styles.divider} />
            </div>
            <CategoryDistributionTable 
              cohortResponses={cohortResponses}
              allCompetenciesData={allCompetenciesData}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default CohortReportWeb;
